import logger from './logger';

/**
 * Safe localStorage utility functions
 * Handles cases where localStorage is not available (service workers, iframes, etc.)
 */

// Résultat positif mis en cache : la sonde d'écriture ne tourne qu'une fois
// par chargement de page, pas à chaque lecture. Le cas négatif n'est pas mis
// en cache (le stockage peut redevenir disponible).
let storageKnownAvailable = false;

// Quota dépassé : le stockage existe, il est seulement plein. Lire, supprimer
// (donc libérer de la place) doit rester possible.
const isQuotaError = (e) =>
  !!e &&
  (e.name === 'QuotaExceededError' ||
    e.name === 'NS_ERROR_DOM_QUOTA_REACHED' ||
    e.code === 22 ||
    e.code === 1014);

/**
 * Check if storage is available
 */
export const isStorageAvailable = () => {
  if (storageKnownAvailable) return true;
  try {
    if (typeof window === 'undefined') return false;
    if (typeof Storage === 'undefined') return false;
    if (!window.localStorage) return false;
    
    // Test write/read
    const testKey = '__storage_test__';
    window.localStorage.setItem(testKey, 'test');
    window.localStorage.removeItem(testKey);
    storageKnownAvailable = true;
    return true;
  } catch (e) {
    try {
      if (isQuotaError(e) && window.localStorage.length > 0) {
        storageKnownAvailable = true;
        return true;
      }
    } catch {
      // localStorage inaccessible : indisponible.
    }
    return false;
  }
};

/** Réservé aux tests : oublie le résultat mis en cache. */
export const __resetStorageAvailabilityForTests = () => {
  storageKnownAvailable = false;
};

/**
 * Safely get item from localStorage
 */
export const safeGetItem = (key) => {
  try {
    if (!isStorageAvailable()) {
      logger.warn(`localStorage not available, cannot get key: ${key}`);
      return null;
    }
    return window.localStorage.getItem(key);
  } catch (error) {
    logger.warn(`Error getting localStorage key ${key}:`, error);
    return null;
  }
};

/**
 * Safely set item in localStorage
 */
export const safeSetItem = (key, value) => {
  try {
    if (!isStorageAvailable()) {
      logger.warn(`localStorage not available, cannot set key: ${key}`);
      return false;
    }
    window.localStorage.setItem(key, value);
    return true;
  } catch (error) {
    logger.warn(`Error setting localStorage key ${key}:`, error);
    return false;
  }
};

/**
 * Safely remove item from localStorage
 */
export const safeRemoveItem = (key) => {
  try {
    if (!isStorageAvailable()) {
      logger.warn(`localStorage not available, cannot remove key: ${key}`);
      return false;
    }
    window.localStorage.removeItem(key);
    return true;
  } catch (error) {
    logger.warn(`Error removing localStorage key ${key}:`, error);
    return false;
  }
};

/**
 * Safely get item from sessionStorage
 */
export const safeGetSessionItem = (key) => {
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) {
      return null;
    }
    return window.sessionStorage.getItem(key);
  } catch (error) {
    logger.warn(`Error getting sessionStorage key ${key}:`, error);
    return null;
  }
};

/**
 * Safely set item in sessionStorage
 */
export const safeSetSessionItem = (key, value) => {
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) {
      return false;
    }
    window.sessionStorage.setItem(key, value);
    return true;
  } catch (error) {
    logger.warn(`Error setting sessionStorage key ${key}:`, error);
    return false;
  }
};

/**
 * Safely remove item from sessionStorage
 */
export const safeRemoveSessionItem = (key) => {
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) {
      return false;
    }
    window.sessionStorage.removeItem(key);
    return true;
  } catch (error) {
    logger.warn(`Error removing sessionStorage key ${key}:`, error);
    return false;
  }
};

/**
 * Get safe storage interface for Supabase
 * IMPORTANT: With PKCE, Supabase stores the code verifier in sessionStorage automatically
 * Supabase uses keys like: sb-<project-ref>-auth-token-code-verifier for PKCE
 * 
 * The storage adapter must:
 * 1. Check sessionStorage first (for PKCE code verifier)
 * 2. Fall back to localStorage (for session tokens)
 * 3. Store in the appropriate storage based on the key pattern
 */
export const getSafeStorage = () => {
  const storage = {
    getItem: (key) => {
      if (!key) return null;
      
      // Try sessionStorage first (Supabase stores PKCE code verifier here)
      try {
        if (typeof window !== 'undefined' && window.sessionStorage) {
          const sessionValue = window.sessionStorage.getItem(key);
          if (sessionValue !== null) {
            return sessionValue;
          }
        }
      } catch {
        // sessionStorage not available or blocked
      }

      // Try localStorage (for session tokens and other data)
      try {
        if (typeof window !== 'undefined' && window.localStorage) {
          return window.localStorage.getItem(key);
        }
      } catch {
        // localStorage not available or blocked
      }
      
      return null;
    },
    setItem: (key, value) => {
      if (!key) return;
      
      // Supabase uses keys ending with '-code-verifier' for PKCE code verifier
      // These should be stored in sessionStorage
      // All other keys (including session tokens) go to localStorage
      const isPKCECodeVerifier = key && key.endsWith('-code-verifier');
      
      try {
        if (isPKCECodeVerifier) {
          // Store PKCE code verifier in sessionStorage
          if (typeof window !== 'undefined' && window.sessionStorage) {
            window.sessionStorage.setItem(key, value);
            return;
          }
        }
        
        // Default to localStorage for all other keys (sessions, etc.)
        if (typeof window !== 'undefined' && window.localStorage) {
          window.localStorage.setItem(key, value);
        }
      } catch (e) {
        logger.warn(`Error setting storage key ${key} (isPKCE: ${isPKCECodeVerifier}):`, e);
      }
    },
    removeItem: (key) => {
      if (!key) return;
      
      // Remove from both storages to ensure cleanup
      try {
        if (typeof window !== 'undefined' && window.sessionStorage) {
          window.sessionStorage.removeItem(key);
        }
        if (typeof window !== 'undefined' && window.localStorage) {
          window.localStorage.removeItem(key);
        }
      } catch (e) {
        logger.warn(`Error removing storage key ${key}:`, e);
      }
    },
  };
  
  return storage;
};

